import { PropType } from 'vue';
import plantsClient from '@/core/services/plants';
import { Options, Vue } from 'vue-class-component';
import {
  BuildingDTO,
  ApartmentDTO,
  BuildingCreateDTO,
  EntrancePlantDetailDTO,
  AddressingType,
  FloorDTO,
} from '@/../client-generator/generated-clients/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import NumberButtons from '@/views/components/NumberButtons/NumberButtons.vue';
import { mapState } from 'vuex';
import { toLabel } from '@/core/utils/EntrancePlant/denomination-helper';

@Options({
  name: 'DialogAddEditApartment',
  props: {
    building: {
      type: Object as PropType<BuildingDTO>,
      required: true,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    apartmentToEdit: {
      type: Object as PropType<ApartmentDTO>,
      required: false,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.invalidNameReason = '';
        this.isValidName = true;
        this.initApartment();
      }
    },
    'apartment.name': function () {
      if (this.apartment) {
        this.verifyValidityOfName();
      }
    },
    'apartment.riserId': function () {
      if(this.isBrfa)
        this.loadRisersFloorApartment();
    }
  },
  components: {
    NumberButtons,
  },
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogAddEditApartment extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  building!: BuildingDTO;
  apartmentToEdit!: ApartmentDTO;

  apartment: ApartmentDTO = new ApartmentDTO();

  newApartments = 0;

  isVisible!: boolean;
  isDialogVisible = false;
  isEdit = false;
  riserFloors: { floor: FloorDTO; apartments: ApartmentDTO[] }[] = [];
  isBrfa = false;

  // Naming validation props
  isValidName = true;
  invalidNameReason: string = '';

  get availableApartments() {
    const totalPlantApartments = this.currentEntrancePlant.buildings?.reduce(
      (acc, building) => acc + building.apartments?.length!,
      0
    ) || 0;
    return 3999 - totalPlantApartments;
  }

  mounted() {
    this.isBrfa =
      this.currentEntrancePlant.addressingType ===
      AddressingType.BuildingRiserFloorApartment;
    this.isDialogVisible = this.isVisible;
  }

  async initApartment() {
    if (this.apartmentToEdit) {
      this.isEdit = true;
      this.apartment = new ApartmentDTO(this.apartmentToEdit);
    } else {
      this.isEdit = false;
      if (this.isBrfa) {
        this.apartment = new ApartmentDTO();
        this.apartment.buildingId = this.building.buildingId;
        this.apartment.riserId = this.building.risers![0].riserId;
        this.loadRisersFloorApartment();
        this.apartment.floorId = this.riserFloors[0].floor.floorId;
        this.apartment.name = this.getNewApartmentName();
      }
      this.newApartments = 1;
    }
  }

  handleHideDialog() {
    this.isDialogVisible = false;
  }

  doHideDialog() {
    this.$emit('hide-dialog');
  }

  get dialogTitle() {
    return this.isEdit
      ? this.$t('AppResourceEP.EditApartment')
      : this.$t('AppResourceEP.AddApartments');
  }

  get inputLabel() {
    let label = '';
    if (this.isEdit) {
      label = `${this.$t('AppResource.NameUpper')} ${this.$t(
        'AppResourceEP.Apartment'
      )} ${this.building.apartments!.indexOf(this.apartmentToEdit) + 1}`;
    }
    return label;
  }

  get buttonSubmitLabel() {
    return this.$t('AppResource.Save');
  }

  get saveButtonDisabled() {
    if (this.isEdit) {
      return !this.isValidName || !this.apartment.name?.trim();
    } else {
      return this.newApartments < 1;
    }
  }

  get isBeenEdited() {
    return !this.isEdit || this.apartmentToEdit.name !== this.apartment.name;
  }

  loadRisersFloorApartment() {
    this.riserFloors = [];
    const floors = this.building.floors!.filter(
      (floor) => floor.riserId === this.apartment.riserId
    );
    for (const floor of floors) {
      const apartments = this.building.apartments!.filter(
        (apart) => apart.floorId === floor.floorId
      );
      this.riserFloors.push({ floor: floor, apartments: apartments });
    }
    if (
      this.apartment.floorId === undefined ||
      this.riserFloors.findIndex(
        (riser) => riser.floor.floorId === this.apartment.floorId
      ) === -1
    ) {
      this.apartment.floorId = this.riserFloors[0].floor.floorId;
      this.updateApartment();
    }
  }

  updateApartment() {
    this.apartment.name = this.getNewApartmentName();
  }

  getNewApartmentName() {
    if (
      this.isEdit &&
      this.apartment.floorId === this.apartmentToEdit.floorId
    ) {
      return this.apartmentToEdit.name;
    } else {
      const riserIndex = this.building.risers!.findIndex(
        (riser) => riser.riserId === this.apartment.riserId
      );
      const floorIndex = this.riserFloors.findIndex(
        (riser) => riser.floor.floorId === this.apartment.floorId
      );
      return `${toLabel(
        this.currentEntrancePlant.buildings!.indexOf(this.building) + 1,
        this.currentEntrancePlant.denominationBuildings,
        this.currentEntrancePlant.brfaBlockDigits
      )}${toLabel(
        riserIndex + 1,
        this.currentEntrancePlant.denominationRisers,
        this.currentEntrancePlant.brfaRiserDigits
      )}${toLabel(
        floorIndex + 1,
        this.currentEntrancePlant.denominationFloors,
        this.currentEntrancePlant.brfaFloorDigits
      )}${toLabel(
        this.riserFloors[floorIndex].apartments.length + 1,
        this.currentEntrancePlant.denominationApartments,
        this.currentEntrancePlant.brfaApartmentDigits
      )}`;
    }
  }

  verifyValidityOfName() {
    if (this.apartment.name === undefined) return;

    const nameToCheck = this.apartment.name.trim();
    let listToCheck = this.building.apartments || [];

    if (this.isEdit) {
      listToCheck = listToCheck.filter(
        (value) => value.apartmentId !== this.apartmentToEdit.apartmentId
      );
    }
    const exist = listToCheck.some(
      (existing) =>
        existing.name?.toLowerCase().trim() == nameToCheck.toLowerCase().trim()
    );
    this.isValidName = !exist;
    this.invalidNameReason = this.isValidName
      ? ''
      : this.$t('general.alreadyExisting');
  }

  async handleButtonSubmitAction() {
    this.isDialogVisible = false;
    if (this.isBeenEdited) {
      await this.saveApartment();
    }
    this.$emit('update-list');
  }

  async saveApartment() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      if (this.isEdit) {
        await plantsClient.apartmentPUT(
          this.currentEntrancePlant.desktopId,
          this.apartment
        );
      } else {
        const newBuildingsApartments = new BuildingCreateDTO({
          buildingId: this.building.buildingId,
          apartmentsCount: this.newApartments,
        });

        if (this.isBrfa) {
          //save brfa apartment
          await plantsClient.apartmentbrfa(
            this.currentEntrancePlant.desktopId,
            this.apartment
          )
        } else {
          await plantsClient.apartmentPOST(
            this.currentEntrancePlant.desktopId,
            newBuildingsApartments
          );
        }
      }
      this.$toast.add({
        severity: 'success',
        summary: this.isEdit
          ? this.$t('entrancePlant.editedApartment')
          : this.$t('entrancePlant.apartmentsBannerCreated'),
        closable: false,
        life: 3000,
      });
    } catch (error) {
      console.log(error);
      setTimeout(() => {
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          closable: false,
          life: 3000,
        });
      }, 1500);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }
}
